import { MONO, SANS } from './fonts';

// Shared building blocks for the signed-in dashboards (/account and /admin).
// Screens and panels compose these into their own `styles` object and add
// whatever is specific to them, so the two dashboards stay visually identical
// without every panel restating the same card and input values.

// --- Screen shell -----------------------------------------------------------

export const page = {
  minHeight: '100vh',
  background: '#f3f3f1',
  padding: '48px 20px 96px',
  fontFamily: SANS,
};

export const container = {
  maxWidth: 640,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
};

export const header = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingBottom: 24,
  marginBottom: 4,
  borderBottom: '1px solid #e0e0da',
};

export const headerTitle = {
  fontFamily: MONO,
  fontSize: '1.3rem',
  fontWeight: 700,
  color: '#111',
  margin: 0,
};

export const headerSubtitle = {
  fontSize: '0.85rem',
  color: '#888',
  margin: '4px 0 0',
};

// --- Tabs -------------------------------------------------------------------

export const tabRow = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginBottom: 4,
};

// Borders here are longhands, and tabActive below overrides the same longhand.
// Mixing them — `border` here and `borderColor` there — leaves a tab that has
// been active with width and style but no colour once React removes the
// longhand, so it falls back to currentColor and renders dark grey.
export const tab = {
  padding: '8px 14px',
  borderRadius: 999,
  borderWidth: 1,
  borderStyle: 'solid',
  // Matches the card border, so the pills don't carry more weight than the
  // content sitting under them.
  borderColor: '#e5e5ea',
  background: '#fff',
  color: '#555',
  fontSize: '0.85rem',
  fontWeight: 500,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
};

export const tabActive = {
  background: '#111',
  borderColor: '#111',
  color: '#fff',
  fontWeight: 600,
};

// Unread count on a tab. Inherits nothing from the tab's own colours so it
// stays legible on both the dark active pill and the light inactive one.
export const tabCount = {
  background: '#c0392b',
  color: '#fff',
  fontSize: '0.68rem',
  fontWeight: 700,
  lineHeight: 1,
  padding: '3px 6px',
  borderRadius: 999,
};

// --- Cards ------------------------------------------------------------------

export const card = {
  width: '100%',
  background: '#fff',
  border: '1px solid #e5e5ea',
  borderRadius: 16,
  padding: 32,
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  fontFamily: SANS,
  boxSizing: 'border-box',
};

export const cardTitle = {
  fontFamily: MONO,
  fontSize: '1.05rem',
  marginBottom: 8,
};

export const cardDescription = {
  fontSize: '0.85rem',
  color: '#777',
  lineHeight: 1.6,
  margin: '0 0 20px',
};

export const sectionLabel = {
  fontSize: '0.78rem',
  fontWeight: 600,
  color: '#555',
  margin: '4px 0 8px',
};

export const hint = {
  color: '#999',
  fontSize: '0.75rem',
  lineHeight: 1.5,
  margin: '-6px 0 12px',
};

// --- Form controls ----------------------------------------------------------

export const input = {
  width: '100%',
  padding: '10px 12px',
  marginBottom: 12,
  borderRadius: 8,
  border: '1px solid #d5d5da',
  fontSize: '0.95rem',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

export const textarea = {
  ...input,
  fontSize: '0.9rem',
  resize: 'vertical',
  minHeight: 60,
};

// The chevron is an inline data: URI so the select needs no extra asset and
// no network request.
export const select = {
  width: '100%',
  padding: '10px 36px 10px 12px',
  marginBottom: 12,
  borderRadius: 8,
  border: '1px solid #d5d5da',
  fontSize: '0.9rem',
  boxSizing: 'border-box',
  background: '#fff',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23666' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 14px center',
  backgroundSize: '11px 7px',
};

export const primaryButton = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: 'none',
  background: '#111',
  color: '#fff',
  fontSize: '0.95rem',
  cursor: 'pointer',
};

export const secondaryButton = {
  padding: '10px 18px',
  borderRadius: 8,
  border: '1px solid #d5d5da',
  background: '#fff',
  color: '#333',
  fontSize: '0.88rem',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export const dashedButton = {
  width: '100%',
  marginBottom: 12,
  padding: '9px 12px',
  borderRadius: 8,
  border: '1px dashed #c5c5cc',
  background: '#fafafa',
  color: '#555',
  fontSize: '0.85rem',
  cursor: 'pointer',
};

export const iconButton = {
  background: 'none',
  border: 'none',
  color: '#aaa',
  fontSize: '1.2rem',
  lineHeight: 1,
  cursor: 'pointer',
  padding: '0 4px',
  flexShrink: 0,
};

export const disabled = {
  opacity: 0.5,
  cursor: 'default',
};

// --- Lists ------------------------------------------------------------------

export const list = {
  listStyle: 'none',
  margin: '20px 0 0',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

// Wraps rather than squashing. The trailing controls are all flexShrink: 0, so
// without this the description is the only thing that can give — and on a phone
// it collapses to a two-word column. With a flex-basis on the text instead, the
// controls drop to their own line once there isn't room for both. Desktop is
// unaffected, since there the row always has space.
export const listItem = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
  padding: '12px 14px',
  background: '#f9f9f7',
  border: '1px solid #ececE6',
  borderRadius: 10,
  fontSize: '0.85rem',
};

export const listMain = {
  flex: '1 1 220px',
  minWidth: 0,
};

export const listMeta = {
  color: '#888',
  fontSize: '0.75rem',
  marginTop: 2,
};

export const empty = {
  color: '#999',
  fontSize: '0.85rem',
  margin: '20px 0 0',
};

// --- Status -----------------------------------------------------------------

export const badge = {
  fontSize: '0.7rem',
  fontWeight: 600,
  padding: '3px 8px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

export const BADGE_COLORS = {
  sent: { background: '#fef3c7', color: '#92400e' },
  accepted: { background: '#d1fae5', color: '#065f46' },
  declined: { background: '#fee2e2', color: '#991b1b' },
  withdrawn: { background: '#eeeeea', color: '#77776f' },
};

// Per-field validation, matching ContactScreen: a red border on the offending
// input plus a short message under it, cleared as soon as the field changes.
//
// The full shorthand, not just borderColor: every input this is spread onto
// declares `border` as a shorthand, and swapping a longhand in and out of one
// leaves the field with no border colour once the error clears.
export const inputError = {
  border: '1px solid rgba(248,113,113,0.5)',
};

export const fieldError = {
  fontSize: '0.72rem',
  color: '#f87171',
  margin: '-8px 0 12px',
};

export const success = {
  color: '#1a7f37',
  fontSize: '0.85rem',
  marginTop: 12,
};

export const error = {
  color: '#c0392b',
  fontSize: '0.85rem',
  marginTop: 12,
};

// --- Money ------------------------------------------------------------------

export const priceInputWrap = {
  position: 'relative',
};

export const priceSymbol = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#888',
  fontSize: '0.9rem',
  pointerEvents: 'none',
};

export const priceInput = {
  width: '100%',
  padding: '10px 12px 10px 26px',
  borderRadius: 8,
  border: '1px solid #d5d5da',
  fontSize: '0.9rem',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

export const totalRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  padding: '12px 0',
  marginBottom: 12,
  borderTop: '1px solid #ececE6',
  fontFamily: MONO,
};

export const totalValue = {
  fontSize: '1.1rem',
  fontWeight: 700,
};
